import { useState, useRef, useEffect, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { X, User, Users, Building2 } from "lucide-react";

interface ContactSuggestion {
  name: string;
  email: string;
  type: "user" | "lead" | "client";
}

interface CcEmailInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  "data-testid"?: string;
}

function TypeIcon({ type }: { type: string }) {
  if (type === "user") return <Users className="w-3 h-3 text-blue-500" />;
  if (type === "lead") return <User className="w-3 h-3 text-orange-500" />;
  return <Building2 className="w-3 h-3 text-green-500" />;
}

function TypeLabel({ type }: { type: string }) {
  if (type === "user") return <span className="text-xs text-blue-600 dark:text-blue-400 font-medium shrink-0">Team</span>;
  if (type === "lead") return <span className="text-xs text-muted-foreground capitalize shrink-0">Lead</span>;
  return <span className="text-xs text-muted-foreground capitalize shrink-0">Client</span>;
}

export function CcEmailInput({ value, onChange, label = "CC", placeholder = "Add CC recipients…", "data-testid": testId }: CcEmailInputProps) {
  const [inputText, setInputText] = useState("");
  const [suggestions, setSuggestions] = useState<ContactSuggestion[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState(-1);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const emails: string[] = value ? value.split(",").map(e => e.trim()).filter(Boolean) : [];

  const fetchSuggestions = useCallback(async (q: string) => {
    if (!q || q.length < 1) { setSuggestions([]); setShowDropdown(false); return; }
    setLoading(true);
    try {
      const res = await fetch(`/api/email/contacts-lookup?q=${encodeURIComponent(q)}`, { credentials: "include" });
      if (res.ok) {
        const data: ContactSuggestion[] = await res.json();
        const existingEmails = new Set(emails.map(e => e.toLowerCase()));
        const filtered = data.filter(s => !existingEmails.has(s.email.toLowerCase()));
        setSuggestions(filtered);
        setShowDropdown(filtered.length > 0);
      }
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, [emails.join(",")]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setInputText(text);
    setHighlightedIdx(-1);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = text.split(",").pop()?.trim() || "";
    debounceRef.current = setTimeout(() => fetchSuggestions(q), 200);
  };

  const addEmail = (email: string) => {
    const trimmed = email.trim();
    if (!trimmed) return;
    const existing = emails.map(e => e.toLowerCase());
    if (existing.includes(trimmed.toLowerCase())) return;
    const newEmails = [...emails, trimmed];
    onChange(newEmails.join(", "));
    setInputText("");
    setSuggestions([]);
    setShowDropdown(false);
    setHighlightedIdx(-1);
  };

  const removeEmail = (idx: number) => {
    const newEmails = emails.filter((_, i) => i !== idx);
    onChange(newEmails.join(", "));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIdx(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIdx(i => Math.max(i - 1, -1));
    } else if (e.key === "Enter" || e.key === "Tab" || e.key === ",") {
      if (showDropdown && highlightedIdx >= 0 && suggestions[highlightedIdx]) {
        e.preventDefault();
        addEmail(suggestions[highlightedIdx].email);
      } else {
        const typedEmail = inputText.replace(/,$/, "").trim();
        if (typedEmail) {
          e.preventDefault();
          addEmail(typedEmail);
        }
      }
    } else if (e.key === "Backspace" && !inputText && emails.length > 0) {
      removeEmail(emails.length - 1);
    } else if (e.key === "Escape") {
      setShowDropdown(false);
      setHighlightedIdx(-1);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setShowDropdown(false);
        const typedEmail = inputText.trim();
        if (typedEmail) { addEmail(typedEmail); }
      }
    }, 150);
  };

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  return (
    <div className="space-y-1.5" ref={containerRef}>
      {label && (
        <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </Label>
      )}
      <div className="relative">
        <div
          className="flex flex-wrap gap-1 min-h-9 px-3 py-1.5 rounded-md border border-input bg-background text-sm ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 cursor-text"
          onClick={() => inputRef.current?.focus()}
        >
          {emails.map((email, idx) => (
            <Badge
              key={idx}
              variant="secondary"
              className="h-6 gap-1 pr-1 text-xs font-normal"
              data-testid={`cc-tag-${idx}`}
            >
              <span className="max-w-[160px] truncate">{email}</span>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeEmail(idx); }}
                className="ml-0.5 rounded-sm opacity-60 hover:opacity-100 focus:outline-none"
                data-testid={`btn-remove-cc-${idx}`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={inputText}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onFocus={() => { if (suggestions.length > 0) setShowDropdown(true); }}
            placeholder={emails.length === 0 ? placeholder : ""}
            className="flex-1 min-w-[140px] bg-transparent outline-none placeholder:text-muted-foreground text-sm py-0.5"
            data-testid={testId || "input-cc-email"}
          />
        </div>

        {showDropdown && suggestions.length > 0 && (
          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-md shadow-md max-h-72 overflow-y-auto">
            {suggestions.map((s, idx) => (
              <button
                key={s.email}
                type="button"
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                  idx === highlightedIdx ? "bg-accent text-accent-foreground" : "hover:bg-accent hover:text-accent-foreground"
                }`}
                onMouseDown={(e) => { e.preventDefault(); addEmail(s.email); }}
                onMouseEnter={() => setHighlightedIdx(idx)}
                data-testid={`cc-suggestion-${idx}`}
              >
                <TypeIcon type={s.type} />
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{s.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{s.email}</div>
                </div>
                <TypeLabel type={s.type} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
